// perception.js — Pure helpers for the Hidden Discovery system.
//
// No React, no Firestore — just math used by submitPerceptionResponse,
// trap path checks, and per-viewer visibility filters. Lighting / line-of-
// sight is deliberately NOT modeled here in v1 — straight-line distance only.
// When the vision/LoS pass lands, replace `withinSight` with a wall-aware
// variant; nothing else needs to change.

import { mod } from './minigames';

// Sight ranges (in grid cells). Tunable.
// Passive is intentionally short — it's the "I notice it without trying" radius.
// Active is broader because the player is actively scanning.
export const PASSIVE_PERCEPTION_RANGE = 2;
export const ACTIVE_PERCEPTION_RANGE  = 12;

/** Player's passive perception. Prefers the explicit field they set in
 *  CharacterSetup (which can include proficiency / bonuses we don't track
 *  here); falls back to 10 + WIS mod when no explicit value is set. */
export function passivePerception(playerRecord) {
  if (playerRecord?.passivePerception != null) {
    return playerRecord.passivePerception;
  }
  const wis = playerRecord?.abilityScores?.wis ?? 10;
  return 10 + mod(wis);
}

/** Chebyshev distance — diagonals cost 1, matching the existing path math. */
export function tokenDistance(a, b) {
  if (!a || !b) return Infinity;
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** Cheap sight check. Replace with wall-aware version when vision lands. */
export function withinSight(viewerToken, targetXY, range) {
  if (!viewerToken || !targetXY) return false;
  return tokenDistance(viewerToken, targetXY) <= range;
}

/**
 * Resolve which hidden things a perception roll discovers for a player.
 * Pure — no writes. Caller persists `discoveredBy` updates on the returned ids.
 *
 * @param {Object} args
 * @param {Object} args.viewerToken          { x, y } of the rolling player's token.
 * @param {number} args.roll                 The result they got (passive or active).
 * @param {boolean} args.isPassive           Affects sight range used.
 * @param {string} args.playerId             For dedupe against existing discoveredBy.
 * @param {Array<{id, x, y, discoveryDC, triggered, discoveredBy}>} args.traps
 * @param {Array<{id, x, y, discoveryDC, secretRevealed, discoveredBy}>} args.secretDoors
 *        — for doors, x/y is the midpoint of the door segment.
 * @param {Array<{id, x, y, stealthDC, discoveredBy}>} args.stealthedTokens
 * @returns {{ trapIds: string[], doorIds: string[], tokenIds: string[] }}
 */
export function resolveDiscoveries({
  viewerToken,
  roll,
  isPassive,
  playerId,
  traps = [],
  secretDoors = [],
  stealthedTokens = [],
}) {
  const range = isPassive ? PASSIVE_PERCEPTION_RANGE : ACTIVE_PERCEPTION_RANGE;
  const trapIds = [];
  const doorIds = [];
  const tokenIds = [];

  for (const t of traps) {
    if (t.discoveryDC == null) continue;
    if (t.triggered) continue;
    if (t.discoveredBy?.[playerId]) continue;
    if (!withinSight(viewerToken, t, range)) continue;
    if (roll >= t.discoveryDC) trapIds.push(t.id);
  }

  for (const d of secretDoors) {
    if (d.discoveryDC == null) continue;
    if (d.secretRevealed) continue;
    if (d.discoveredBy?.[playerId]) continue;
    if (!withinSight(viewerToken, d, range)) continue;
    if (roll >= d.discoveryDC) doorIds.push(d.id);
  }

  for (const tk of stealthedTokens) {
    if (tk.stealthDC == null) continue;
    if (tk.discoveredBy?.[playerId]) continue;
    if (!withinSight(viewerToken, tk, range)) continue;
    if (roll >= tk.stealthDC) tokenIds.push(tk.id);
  }

  return { trapIds, doorIds, tokenIds };
}
